import React, { useCallback, useState, useMemo, useEffect } from "react";
import InputGroup from "../Common/InputGroup";
import { useIntl, defineMessages, FormattedMessage } from "react-intl";
import styled from "styled-components";
import { Button } from "react-bootstrap";
import SelectGroup from "../Common/SelectGroup";
import HelpText from "../Common/HelpText";
import { usePropertyLocales } from "../../../../components/property-locale-provider";
import { StyledSpinner, StyledSpinnerButton } from "../EntityForm/EntityForm";
import { useGrpcClient, usePropertyGrpcCall } from "../../../../lib/grpc";
import { EmailConfigAPIPromiseClient } from "../../../../gen/witbooking/backoffice/emailconfig/v1/emailconfig_api_grpc_web_pb";
import {
  CreateEmailConfigRequest,
  EmailConfig,
  GetEmailConfigRequest,
} from "../../../../gen/witbooking/backoffice/emailconfig/v1/emailconfig_api_pb";
import { useLocaleProvider } from "../../../../components/locale-provider";

const messages = defineMessages({
  reservationRecipientsHelpText: {
    id: "emailConfiguration.helpText.reservationRecipients",
    defaultMessage:
      "Listado de las direcciones separadas por , que recibirán copia de la confirmación.",
  },
  fromNameHelpText: {
    id: "emailConfiguration.helpText.fromName",
    defaultMessage: "Indicar el nombre que aparecerá como remitente de los correos.",
  },
  languageEmailHelpText: {
    id: "emailConfiguration.helpText.languageEmail",
    defaultMessage:
      "Seleccionar el idioma en el que el hotel desea recibir la copia de la confirmación.",
  },
  fromEmailHelpText: {
    id: "emailConfiguration.helpText.fromEmail",
    defaultMessage: "Sender e-mail address",
  },
  fromEmailHelpTextLink: {
    id: "emailConfiguration.helpText.fromEmailLink",
    defaultMessage: "Cómo configurar una cuenta de correo como remitente de los emails automáticos",
  },
  replyToHelpText: {
    id: "emailConfiguration.helpText.replyTo",
    defaultMessage: "Email address that will receive the mail in case the customer clicks on reply",
  },
  reservationRecipients: {
    id: "emailConfiguration.reservationRecipients",
    defaultMessage: "Receptores de confirmación de reserva",
  },
  fromName: {
    id: "emailConfiguration.fromName",
    defaultMessage: "Nombre del Remitente de los correos",
  },
  languageEmail: {
    id: "emailConfiguration.languageEmail",
    defaultMessage: "Idioma del correo para el hotel",
  },
  fromEmail: {
    id: "emailConfiguration.fromEmail",
    defaultMessage: "Sender",
  },
  replyTo: {
    id: "emailConfiguration.replyTo",
    defaultMessage: "Responder a",
  },
  save: {
    id: "emailConfiguration.save",
    defaultMessage: "Guardar",
  },
  check: {
    id: "emailConfiguration.check",
    defaultMessage: "Comprobar",
  },
  ca: {
    id: "emailConfiguration.language.ca",
    defaultMessage: "Catalán",
  },
  es: {
    id: "emailConfiguration.language.es",
    defaultMessage: "Spanish",
  },
  eu: {
    id: "emailConfiguration.language.eu",
    defaultMessage: "Euskera",
  },
  de: {
    id: "emailConfiguration.language.de",
    defaultMessage: "Deutch",
  },
  fr: {
    id: "emailConfiguration.language.fr",
    defaultMessage: "French",
  },
  it: {
    id: "emailConfiguration.language.it",
    defaultMessage: "Italian",
  },
  nl: {
    id: "emailConfiguration.language.nl",
    defaultMessage: "Dutch",
  },
  ko: {
    id: "emailConfiguration.language.ko",
    defaultMessage: "Korean",
  },
  pt: {
    id: "emailConfiguration.language.pt",
    defaultMessage: "Portuguese",
  },
  ru: {
    id: "emailConfiguration.language.ru",
    defaultMessage: "Russian",
  },
  da: {
    id: "emailConfiguration.language.da",
    defaultMessage: "Danish",
  },
  en: {
    id: "emailConfiguration.language.en",
    defaultMessage: "English",
  },
  zh: {
    id: "emailConfiguration.language.zh",
    defaultMessage: "Chinese",
  },
  hu: {
    id: "emailConfiguration.language.hu",
    defaultMessage: "Hungarian",
  },
  ja: {
    id: "emailConfiguration.language.ja",
    defaultMessage: "Japanese",
  },
  ro: {
    id: "emailConfiguration.language.ro",
    defaultMessage: "Romanian",
  },
  defaultOption: {
    id: "emailConfiguration.defaultOption",
    defaultMessage: "Original de la copia del cliente",
  },
  saveSuccess: {
    id: "default.saveSuccess",
    defaultMessage: "Saved successfully",
  },
  saveError: {
    id: "default.saveErrorMessage",
    defaultMessage: "Ha ocurrido un error",
  },
  formEmptyFieldsError: {
    id: "default.formEmptyFieldsError",
    defaultMessage: "Required fields are missing",
  },
  invalidRecipientsError: {
    id: "emailConfiguration.invalidRecipientsError",
    description: "Reservation receivers contain an invalid email address (B4-3)",
    defaultMessage: "Some reservation receiver addresses are not valid emails: {emails}",
  },
});

const InputContainer = styled.fieldset`
  display: flex;
  label {
    margin-right: 10px;
    min-width: 250px;
    font-weight: inherit;
  }
  > div {
    flex: 1;
  }
  .componentGroupStyled {
    width: 60%;
  }
`;

const StyledButton = styled(Button)`
  button {
    margin-bottom: 50px;
  }
`;

const RecipientsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ButtonContainer = styled.div`
  margin: 10px 0;
`;

const HelpTextStyled = styled(HelpText)`
  margin: 5px 0 15px 15px !important;
`;

const SuccessMessage = styled.div`
  margin-top: 20px;
`;

// Basic email-format check for the comma-separated reservation receivers (B4-3).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function invalidReservationRecipients(value) {
  return (value || "")
    .split(",")
    .map(entry => entry.trim())
    .filter(Boolean)
    .filter(entry => !EMAIL_REGEX.test(entry));
}

function emailConfigFromObject(emailConfig) {
  const result = new EmailConfig();
  // Split into trimmed, non-empty addresses. An empty field (allowed on aggregators) yields an
  // empty list instead of [""], and stray/trailing commas are dropped.
  const reservationReceivers = (emailConfig.reservationRecipients || "")
    .split(",")
    .map(entry => entry.trim())
    .filter(Boolean);
  result.setEmailsReservationReceiversList(reservationReceivers);
  result.setLanguageHotelEmail(emailConfig.languageEmail);
  result.setFromName(emailConfig.fromName);
  result.setFromEmail(emailConfig.fromEmail?.trim());
  result.setWithConditions(true);
  result.setWithRateSpecification(true);
  result.setWithAgent(true);
  result.setReplyTo(emailConfig.replyTo?.trim());

  return result;
}

export default function EmailConfiguration({ isWitAdmin, isAggregator }) {
  const locale = useLocaleProvider();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showRequiredFieldsErrorMessage, setShowRequiredFieldsErrorMessage] = useState(false);
  const [invalidRecipients, setInvalidRecipients] = useState([]);
  const [isSubmit, setIsSubmit] = useState(false);
  const [emailConfigurationState, setEmailConfigurationState] = useState({
    languageEmail: "RESERVATION_LOCALE",
  });

  const intl = useIntl();
  const propertyLocales = usePropertyLocales();
  const grpcClient = useGrpcClient(EmailConfigAPIPromiseClient);
  const getEmailConfig = usePropertyGrpcCall(grpcClient, grpcClient.getEmailConfig);
  const updateEmailConfig = usePropertyGrpcCall(grpcClient, grpcClient.createEmailConfig);

  useEffect(() => {
    async function handleGetEmailConfig() {
      const request = new GetEmailConfigRequest();
      const response = await getEmailConfig(request);
      const emailConfiguration = response.getEmailconfig().toObject();
      const newConfig = {
        reservationRecipients: emailConfiguration.emailsReservationReceiversList.join(","),
        fromEmail: emailConfiguration.fromEmail,
        fromName: emailConfiguration.fromName,
        languageEmail: emailConfiguration.languageHotelEmail || "RESERVATION_LOCALE",
        replyTo: emailConfiguration.replyTo,
      };
      setEmailConfigurationState(newConfig);
    }
    handleGetEmailConfig();
  }, [getEmailConfig]);

  const handleChange = useCallback(
    ({ event, field }) => {
      const value = event.target.value;
      setEmailConfigurationState({
        ...emailConfigurationState,
        [field]: value,
      });
      if (showSuccessMessage) {
        setShowSuccessMessage(false);
      }
      if (showErrorMessage) {
        setShowErrorMessage(false);
      }
      setShowRequiredFieldsErrorMessage(false);
      setInvalidRecipients([]);
    },
    [emailConfigurationState, showErrorMessage, showSuccessMessage]
  );

  const handleUpdateEmailConfig = useCallback(async () => {
    // Reservation recipients are optional on aggregators (QA): the aggregator may leave them empty
    // so the central address is not forced. On individual engines the field stays mandatory.
    const requiredFields = isAggregator
      ? ["fromName", "replyTo"]
      : ["reservationRecipients", "fromName", "replyTo"];
    if (requiredFields.some(it => !emailConfigurationState[it])) {
      setShowRequiredFieldsErrorMessage(true);
      return;
    }
    // B4-3: block save if any reservation receiver is not a well-formed email address.
    const badRecipients = invalidReservationRecipients(emailConfigurationState.reservationRecipients);
    if (badRecipients.length > 0) {
      setInvalidRecipients(badRecipients);
      return;
    }
    setInvalidRecipients([]);
    setIsSubmit(true);
    const request = new CreateEmailConfigRequest();
    request.setEmailconfig(emailConfigFromObject(emailConfigurationState));
    try {
      await Promise.all([updateEmailConfig(request)]);
      setShowSuccessMessage(true);
    } catch (error) {
      setShowErrorMessage(true);
    }
    setIsSubmit(false);
    setShowRequiredFieldsErrorMessage(false);
  }, [updateEmailConfig, emailConfigurationState, isAggregator]);

  const optionLocales = useMemo(
    () =>
      propertyLocales.reduce(
        (result, locale) => {
          result = {
            ...result,
            [locale]: {
              label: intl.formatMessage(messages[locale]),
            },
          };

          return result;
        },
        {
          RESERVATION_LOCALE: {
            label: intl.formatMessage(messages.defaultOption),
          },
        }
      ),
    [intl, propertyLocales]
  );

  const linkByLocale = useMemo(() => {
    switch (locale.currentLocale) {
      case "en-US":
        return "https://witbooking.freshdesk.com/en/support/solutions/articles/101000410491-how-to-set-up-an-email-account-as-a-sender-for-automatic-emails";
      case "fr-FR":
        return "https://witbooking.freshdesk.com/en/support/solutions/articles/101000410491-how-to-set-up-an-email-account-as-a-sender-for-automatic-emails";
      case "es-ES":
        return "https://witbooking.freshdesk.com/es/support/solutions/articles/101000410491-c%C3%B3mo-configurar-una-cuenta-de-correo-como-remitente-de-los-emails-autom%C3%A1ticos";
      default:
        throw new Error(`invalid locale ${locale.currentLocale}`);
    }
  }, [locale.currentLocale]);

  return (
    <>
      {showErrorMessage && (
        <SuccessMessage>
          <div role="alert" className="alert alert-danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </div>
        </SuccessMessage>
      )}
      {showRequiredFieldsErrorMessage && (
        <SuccessMessage>
          <div role="alert" className="alert alert-danger">
            <strong>
              <FormattedMessage {...messages.formEmptyFieldsError} />
            </strong>
          </div>
        </SuccessMessage>
      )}
      {invalidRecipients.length > 0 && (
        <SuccessMessage>
          <div role="alert" className="alert alert-danger">
            <strong>
              <FormattedMessage
                {...messages.invalidRecipientsError}
                values={{ emails: invalidRecipients.join(", ") }}
              />
            </strong>
          </div>
        </SuccessMessage>
      )}
      {showSuccessMessage && (
        <SuccessMessage>
          <div role="alert" className="alert alert-success">
            <strong>
              <FormattedMessage {...messages.saveSuccess} />
            </strong>
          </div>
        </SuccessMessage>
      )}
      <InputContainer>
        <label>
          <FormattedMessage {...messages.reservationRecipients} />:
        </label>
        <div>
          <RecipientsContainer>
            <InputGroup
              onChange={handleChange}
              field={{
                fieldName: "reservationRecipients",
                className: "componentGroupStyled",
              }}
              value={emailConfigurationState.reservationRecipients}
              hasErrors={
                !isAggregator &&
                showRequiredFieldsErrorMessage &&
                !emailConfigurationState.reservationRecipients
              }
            />
          </RecipientsContainer>
          <HelpTextStyled text={intl.formatMessage(messages.reservationRecipientsHelpText)} />
        </div>
      </InputContainer>
      <InputContainer>
        <label>
          <FormattedMessage {...messages.fromName} />:
        </label>
        <div>
          <InputGroup
            onChange={handleChange}
            field={{
              fieldName: "fromName",
              className: "componentGroupStyled",
            }}
            value={emailConfigurationState.fromName}
            hasErrors={showRequiredFieldsErrorMessage && !emailConfigurationState.fromName}
          />
          <HelpTextStyled text={intl.formatMessage(messages.fromNameHelpText)} />
        </div>
      </InputContainer>
      <InputContainer>
        <label>
          <FormattedMessage {...messages.languageEmail} />:
        </label>
        <div>
          <SelectGroup
            onChange={handleChange}
            field={{
              className: "componentGroupStyled",
              fieldName: "languageEmail",
              widget: "selector",
              optionDisplay: optionLocales,
            }}
            value={emailConfigurationState.languageEmail}
          />
          <HelpTextStyled text={intl.formatMessage(messages.languageEmailHelpText)} />
        </div>
      </InputContainer>
      <InputContainer>
        <label>
          <FormattedMessage {...messages.replyTo} />:
        </label>
        <div>
          <InputGroup
            onChange={handleChange}
            field={{
              fieldName: "replyTo",
              className: "componentGroupStyled",
            }}
            value={emailConfigurationState.replyTo}
            hasErrors={showRequiredFieldsErrorMessage && !emailConfigurationState.replyTo}
          />
          <HelpTextStyled text={intl.formatMessage(messages.replyToHelpText)} />
        </div>
      </InputContainer>
      {isWitAdmin && (
        <InputContainer>
          <label>
            <FormattedMessage {...messages.fromEmail} />:
          </label>
          <div>
            <InputGroup
              onChange={handleChange}
              field={{
                fieldName: "fromEmail",
                className: "componentGroupStyled",
              }}
              value={emailConfigurationState.fromEmail}
            />
            <HelpTextStyled
              text={intl.formatMessage(messages.fromEmailHelpText)}
              className="d-inline-block"
            />{" "}
            <a className="d-inline-block" target="_blank" href={linkByLocale} rel="noreferrer">
              <FormattedMessage {...messages.fromEmailHelpTextLink} />
            </a>
          </div>
        </InputContainer>
      )}
      <ButtonContainer>
        {isSubmit ? (
          <StyledSpinnerButton type="submit" bsClass="btn btn-info" disabled>
            <StyledSpinner className="fa fa-spinner fa-spin" />
          </StyledSpinnerButton>
        ) : (
          <StyledButton className="btn btn-primary" onClick={handleUpdateEmailConfig}>
            <FormattedMessage {...messages.save} />
          </StyledButton>
        )}
      </ButtonContainer>
    </>
  );
}
